<template>
  <div
    v-if="showAssetTray"
    class="asset-tray-container"
    :class="{'expanded': isExpanded}"
  >
    <div class="top-bar">
      <div class="asset-tabs">
        <button
          v-for="tab in tabGroupings"
          :key="tab.id"
          class="tab"
          :class="{ 'active': isActiveTabGrouping(tab.id)}"
          @click="setActiveTabGrouping(tab)"
        >
          <span v-html="stripHtml(tab.name)"></span>
          <icon
            v-if="tab.isNewContent"
            name="circle-filled"
            variant="DC3416"
          />
        </button>
      </div>
      <div class="tray-actions">
        <button @click="expandAssetTray">
          <icon
            name="expand"
          />
        </button>
        <button @click="closeAssetTray">
          <icon
            name="cross-thin"
          />
        </button>
      </div>
    </div>
    <div
      class="asset-content"
      :class="{'expanded-content': isExpanded}"
    >
      <div
        v-for="topic in getActiveTopics()"
        :key="topic.id"
        class="asset-content-topic"
      >
        <span
          class="topic-title"
          v-html="stripHtml(topic.title)"
        ></span>
        <topic
          :topic="topic"
          :show-title="false"
        />
      </div>
    </div>
  </div>
</template>

<script>

import Icon from '@components/singletons/icon';
import Topic from '@/components/topics/topic.vue';

import { stripHtml } from '@/utils';

export default {
  components: { Icon, Topic },
  props: {
    answered: {
      type: Boolean,
    },
    flashcard: {
      type: Object,
    },
  },
  data() {
    return {
      activeTabGrouping: null,
      isExpanded: false,
    };
  },
  computed: {
    flashcardAssetTrayGrouping() {
      const context = this.answered ? 'rationale' : 'question';
      let assetTrayGrouping = null;
      this.flashcard?.assetAssociations.forEach(assetAssociation => {
        if (assetAssociation?.assetAContext === context) {
          const grouping = this.$store.getters['groupings/all'][assetAssociation?.assetBId];
          if (grouping?.groupingType === 'Asset Tray') {
            assetTrayGrouping = grouping;
          }
        }
      });
      return assetTrayGrouping;
    },
    tabGroupings() {
      if(this.flashcardAssetTrayGrouping?.assetAssociations?.length) {
        const sortedAssetTrayAssociations = [...this.flashcardAssetTrayGrouping?.assetAssociations].sort((a, b) => a.order - b.order);
        return sortedAssetTrayAssociations.map(assetAssociation => {
          const tabGrouping = this.$store.getters['groupings/all'][assetAssociation?.assetBId];
          if (!tabGrouping) {
            console.error(`Missing grouping with id of: ${assetAssociation?.assetBId}. Check visibility of product associations in CMS.`);
          }
          tabGrouping.isNewContent = assetAssociation?.displayType === 'new_content';
          return tabGrouping;
        }).filter(tab => tab?.id && tab?.name);
      }
      return [];
    },
    showAssetTray() {
      return this.$store.getters['ui/showAssetTray'] && this.tabGroupings.length;
    },
  },
  watch: {
    showAssetTray(showStatus) {
      if (showStatus) {
        this.activeTabGrouping = this.tabGroupings[0];
      }
    },
  },
  methods: {
    closeAssetTray() {
      const params = {
        'Parent Asset ID': this.flashcard?.id,
        'Parent Category ID': this.flashcard?.categoryIds[0],
        'Parent Asset Type': 'Flashcard',
        'Asset ID': this.flashcardAssetTrayGrouping?.id,
      };
      this.$analytics.events.closedAssetTray(params);

      return this.$store.commit('ui/setShowAssetTray', false);
    },
    expandAssetTray() {
      this.isExpanded = !this.isExpanded;

      const params = {
        'Asset Tray Size': this.isExpanded ? 1 : .25,
        'Parent Asset ID': this.flashcard?.id,
        'Parent Category ID': this.flashcard?.categoryIds[0],
        'Parent Asset Type': 'Flashcard',
        'Asset ID': this.flashcardAssetTrayGrouping?.id,
      };
      this.$analytics.events.resizedAssetTray(params);
    },
    isActiveTabGrouping(tabId) {
      if (this.activeTabGrouping) {
        return tabId === this.activeTabGrouping.id;
      }
    },
    getActiveTopics() {
      if (this.activeTabGrouping?.assetAssociations?.length) {
        return this.activeTabGrouping.assetAssociations.map(assetAssociation => {
          return this.$store.getters['topics/topics'][assetAssociation.assetBId];
        }).sort((a, b) => a.order - b.order);
      }
    },
    setActiveTabGrouping(tab) {
      const params = {
        'Tab Name': tab?.name,
        'Parent Asset ID': this.flashcardAssetTrayGrouping?.id,
        'Parent Asset Type': 'Grouping',
        'Asset ID': tab?.id,
      };
      this.$analytics.events.viewedTabOnAssetTray(params);

      this.activeTabGrouping = tab;
    },
    stripHtml,
  },
};

</script>

<style lang='scss' scoped>
  .asset-tray-container {
    position: fixed;
    left: rem-calc(88); // side-nav
    right: 0;
    bottom: rem-calc(44); // flash-card-footer
    background: #FFFFFF;
    box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.15);
    &.expanded {
      top: rem-calc(180);
    }
    @include breakpoint(small) {
      left: 0;
      top: rem-calc(180);
    }
    .top-bar {
      display: flex;
      align-items: center;
      height: rem-calc(60);
      padding: 0 rem-calc(20);
      border-bottom: 1px solid #D9D9D9;
      background: #FAFAFA;

      .asset-tabs {
        display: flex;
        flex-direction: row;
        min-width: rem-calc(568);
        align-self: flex-end;
        margin: 0 auto;
        .tab {
          height: 48px;
          margin-left: -1px;
          margin-bottom: -1px;
          padding: 0 rem-calc(23);
          border: 1px solid #D9D9D9;
          border-bottom: 0;
          background: transparent;
          border-radius: rem-calc(4) rem-calc(4) 0 0;
          span {
            font-size: rem-calc(14);
            line-height: rem-calc(19);
            font-weight: $semi-bold;
          }
          img {
            position: absolute;
            margin-left: 2px; // Special request to move dot two pixels to the right
          }

          &.active {
            background: #FFFFFF;
            border-bottom: 1px solid #FFFFFF;
            span {
              font-weight: $bold;
            }
          }
        }
      }

      .tray-actions {
        position: absolute;
        right: rem-calc(20);
        button {
          margin-left: rem-calc(5);
          height: rem-calc(28);
          width: rem-calc(28);
          background: transparent;
        }
        @include breakpoint(small) {
          display: none;
        }
      }
    }
    .asset-content {
      height: rem-calc(210);
      width: 100%;
      padding: rem-calc(30) 0;
      overflow-y: auto;
      &.expanded-content {
        height: 100%;
      }

      .asset-content-topic {
        margin: 0 auto;
        padding-left: rem-calc(16);
        max-width: rem-calc(568);

        .topic-title {
          font-weight: $bold;
        }

        /deep/ .top-content {
          padding: 0;
          .content {
            margin: 0;
            p {
              margin-top: rem-calc(5);
              color: #000000;
            }
          }
        }
      }
    }
  }
</style>
